import _ from 'lodash'
import type {ComponentExportStructure, ResolvedDataItem} from '@wix/document-services-types'
import type {DecorationSingleComponentPluginFunction} from '../../types'
import {DecoratedStyleProperties, DecoratedStyleProperty, STYLE_PROPERTY_PREFIXES} from './constants'
import {forEachItemInVariantSplit} from '../../utils/variants'
import {removeEmptyObjects} from '../../utils/dataUtils'

const omitUnneededStyleProperties = (styleProperties: DecoratedStyleProperties): DecoratedStyleProperties =>
    _.omitBy(
        styleProperties,
        (styleProperty: DecoratedStyleProperty) =>
            !styleProperty.value && styleProperty.additionalData?.boxShadowToggleOn === 'false'
    )

const propertyKeyRegex = new RegExp(`^(${STYLE_PROPERTY_PREFIXES.join('|')})-(.+)$`)
const getPropertyKey = (property: string) => {
    const [match, prefix, key] = property.match(propertyKeyRegex) ?? []

    if (!match) {
        return {key: property}
    }

    return {
        key,
        prefix
    }
}

const convertStyleProperties = (styleItem: ResolvedDataItem) => {
    const {style} = styleItem
    if (_.isEmpty(style)) {
        return
    }

    const {properties, propertiesOverride, propertiesSource, ...restProperties} = style
    const newStyle: Record<string, any> = {}
    _.forOwn(properties, (value: string, property: string) => {
        const {key, prefix} = getPropertyKey(property)
        newStyle[key] ??= {}
        if (prefix) {
            newStyle[key].additionalData ??= {}
            newStyle[key].additionalData[prefix] = value
        } else {
            newStyle[key].value = value
        }
    })

    _.forOwn(propertiesOverride, (overrides: Record<string, string>, key: string) => {
        newStyle[key] ??= {}
        newStyle[key].overrides = overrides
    })

    styleItem.style = removeEmptyObjects({
        properties: omitUnneededStyleProperties(newStyle),
        ...restProperties
    })
}

export const pack: DecorationSingleComponentPluginFunction = (component: ComponentExportStructure) => {
    forEachItemInVariantSplit(component.style, convertStyleProperties)
}
